import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStudio } from "@/context/StudioContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

// Returns an error message string, or null when the form is valid.
function validateProjectForm(form, channelId) {
  if (!channelId) return "Select a channel";
  if (!form.title.trim()) return "Title is required";
  return null;
}

export function CreateProjectDialog({ trigger }) {
  const { channels, selectedChannelId, t } = useStudio();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    channel_id: selectedChannelId || "", title: "", brief: "", objective: "",
    format: "short", target_duration: 30, budget_cap: 5, style_id: "",
  });
  const [styles, setStyles] = useState([]);
  useEffect(() => {
    if (open) api.get("/styles").then((r) => setStyles(r.data)).catch(() => {});
  }, [open]);

  const active = channels.find((c) => c.id === (form.channel_id || selectedChannelId));

  const create = async () => {
    const channel_id = form.channel_id || selectedChannelId;
    const err = validateProjectForm(form, channel_id);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { ...form, channel_id, style_id: form.style_id || null });
      toast.success("Project created");
      setOpen(false);
      navigate(`/projects/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="new-video-button"
            className="hidden sm:flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> {t("top.newVideo")}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle>New video project</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Destination channel <span className="text-rose-400">*</span></Label>
            <Select value={form.channel_id || selectedChannelId || ""} onValueChange={(v) => setForm({ ...form, channel_id: v })}>
              <SelectTrigger className={inputCls} data-testid="np-channel"><SelectValue placeholder="Select destination channel" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.data.name} · {c.data.primary_language}</SelectItem>)}
              </SelectContent>
            </Select>
            {active
              ? <p className="text-xs text-slate-500 mt-1">Output language will resolve to {active.data.primary_language} (channel setting).</p>
              : <p className="text-xs text-amber-400/90 mt-1">Pick a destination channel — a video is always created on a specific channel.</p>}
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Title</Label>
            <Input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="np-title" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Brief</Label>
            <Textarea className={inputCls} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} data-testid="np-brief" placeholder="What is this video about?" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Format</Label>
              <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                <SelectTrigger className={inputCls} data-testid="np-format"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  <SelectItem value="short">Short 9:16</SelectItem><SelectItem value="long">Long 16:9</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Duration (s)</Label>
              <Input type="number" className={inputCls} value={form.target_duration} onChange={(e) => setForm({ ...form, target_duration: Number(e.target.value) })} />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Budget ($)</Label>
              <Input type="number" step="0.01" className={inputCls} value={form.budget_cap} onChange={(e) => setForm({ ...form, budget_cap: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Style <span className="text-slate-600">(optional)</span></Label>
            <Select value={form.style_id || "none"} onValueChange={(v) => setForm({ ...form, style_id: v === "none" ? "" : v })}>
              <SelectTrigger className={inputCls} data-testid="np-style"><SelectValue placeholder="No style" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                <SelectItem value="none">No style</SelectItem>
                {styles.map((s) => <SelectItem key={s.id} value={s.id}>{s.data.name} (v{s.version})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="np-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
